// Industry vertical data. Each entry describes genuine, industry-specific
// structural/design considerations (real engineering differences between
// verticals) rather than generic content with the industry name swapped in.

export const industries = [
  {
    slug: "manufacturing",
    name: "Manufacturing",
    tagline: "Production-ready industrial sheds built around your process layout.",
    intro:
      "Manufacturing facilities need structures designed around the actual production line — machinery footprint, material flow, crane access and floor loading all shape the building, not the other way around. We design PEB manufacturing sheds starting from your equipment layout.",
    requirements: [
      { title: "Machinery-driven column spacing", text: "Column grid is planned around equipment placement and material flow, not a default spacing." },
      { title: "Crane provision", text: "Structures needing EOT or gantry cranes require specific runway beam and column design from the outset." },
      { title: "Heavy-duty flooring", text: "VDF flooring rated for machinery point loads and forklift traffic, not standard PCC." },
      { title: "Ventilation for process heat", text: "Ridge ventilators and, where needed, powered exhaust sized to actual process heat load." },
    ],
    faqs: [
      { q: "Can a manufacturing shed be designed for future line expansion?", a: "Yes, if planned at the design stage — end walls can be built as removable/expandable bays to accommodate future production line growth." },
    ],
  },
  {
    slug: "warehousing-logistics",
    name: "Warehousing & Logistics",
    tagline: "High-clear-height, column-efficient structures built for storage density and throughput.",
    intro:
      "Warehousing and logistics facilities prioritize clear height for racking, wide column-free spans for material handling equipment, and — for distribution centres — dock door density for truck throughput. These priorities directly shape the structural design.",
    requirements: [
      { title: "Maximized clear height", text: "Taller structures support multi-level racking, increasing storage density per square foot of land." },
      { title: "Wide column spacing", text: "Fewer internal obstructions mean more flexible racking layouts and equipment movement." },
      { title: "Dock and loading bay design", text: "For distribution-focused facilities, dock door count and yard circulation are planned alongside the structure." },
      { title: "Floor load rating for racking", text: "Flooring specified for the actual point loads of racking legs, not general-purpose use." },
    ],
    faqs: [
      { q: "Should racking layout be decided before the building is designed?", a: "Ideally alongside it — coordinating column grid with intended racking bay dimensions avoids wasted space around mismatched columns." },
    ],
  },
  {
    slug: "pharmaceutical",
    name: "Pharmaceutical",
    tagline: "Structures designed for hygiene-critical, controlled manufacturing environments.",
    intro:
      "Pharmaceutical manufacturing buildings need to support cleanroom-adjacent hygiene standards — flat, sealed flooring, controlled ventilation paths and corrosion-resistant finishes that hold up under strict cleaning protocols. Telangana's Patancheru-Bollaram belt is one of India's established pharma manufacturing hubs.",
    requirements: [
      { title: "Hygiene-grade flooring", text: "Sealed, easily cleanable flooring systems suited to pharma manufacturing hygiene protocols." },
      { title: "Controlled ventilation paths", text: "Structural design accommodates ducting and airflow paths required for cleanroom-adjacent zones." },
      { title: "Corrosion-resistant finishes", text: "Coating specifications that withstand frequent washdown and cleaning-agent exposure." },
      { title: "Precise floor flatness", text: "Flooring tolerances suited to sensitive equipment and material handling within GMP-adjacent areas." },
    ],
    faqs: [
      { q: "Do you handle the cleanroom fit-out itself?", a: "We design and fabricate the structural shell to accommodate your cleanroom and utility requirements; specialized cleanroom fit-out is typically a separate, specialist scope coordinated alongside the structural build." },
    ],
  },
  {
    slug: "food-processing",
    name: "Food Processing",
    tagline: "Washable, drainage-conscious structures for hygiene-sensitive processing.",
    intro:
      "Food processing facilities need washable surfaces, proper floor drainage and pest-resistant detailing — structural decisions that differ meaningfully from general industrial sheds. We design around these hygiene and sanitation requirements from the start.",
    requirements: [
      { title: "Washable wall & floor finishes", text: "Surfaces specified to withstand frequent washdown without degrading." },
      { title: "Floor drainage & slope", text: "Flooring designed with proper drainage slope for processing-area washdown and spill management." },
      { title: "Pest-resistant detailing", text: "Sealed junctions and detailing that minimize pest entry points, a key hygiene compliance factor." },
      { title: "Cold-chain adjacent zones", text: "Structural provisions for insulated cold-storage sections within a larger processing facility, where needed." },
    ],
    faqs: [
      { q: "Can part of a food-processing shed be cold storage and part standard?", a: "Yes — this is common, with insulated PUF panel sections for cold zones integrated into an otherwise standard PEB structure." },
    ],
  },
  {
    slug: "automobile",
    name: "Automobile & Auto Ancillary",
    tagline: "Crane-ready, heavy-floor-load structures for assembly and component manufacturing.",
    intro:
      "Automobile and auto-ancillary manufacturing typically needs crane systems for handling heavy components, high floor-load ratings for assembly equipment, and clear spans that accommodate production line layouts. Growing electronics and auto-ancillary activity near Sri City (close to Tirupati) reflects this sector's expansion in the region.",
    requirements: [
      { title: "Crane systems for component handling", text: "EOT crane runway design for moving heavy parts and assemblies." },
      { title: "High floor-load ratings", text: "Flooring rated for heavy machinery and assembly-line equipment loads." },
      { title: "Production-line clear spans", text: "Column spacing planned around assembly-line layout and vehicle/component movement paths." },
      { title: "Utility routing", text: "Structural coordination for compressed air, power and other utility distribution along production lines." },
    ],
    faqs: [
      { q: "Do auto-ancillary units typically need cranes from day one?", a: "It depends on component weight and handling method — but if there's a realistic chance of future crane need, designing that capacity in from the start avoids a costly retrofit later." },
    ],
  },
  {
    slug: "agriculture",
    name: "Agriculture & Agro-Processing",
    tagline: "Storage and processing structures sized to commodity volumes and trade cycles.",
    intro:
      "Agricultural storage and processing — grain, cotton, chilli, turmeric and other commodity trade — drives significant structural demand across Telangana and Andhra Pradesh, from Nizamabad's turmeric market to Guntur's chilli trade. Storage volume, stacking height and ventilation are the key design drivers.",
    requirements: [
      { title: "High-volume storage design", text: "Clear height and floor area sized to commodity storage and stacking requirements." },
      { title: "Ventilation for stored produce", text: "Natural or powered ventilation to manage moisture and prevent spoilage in stored agricultural goods." },
      { title: "Vehicle access for loading", text: "Wide door openings and yard layout for trucks during peak trading-season loading." },
      { title: "Processing zone provisions", text: "For processing units (rice mills, cotton ginning), structural accommodation for specific processing equipment." },
    ],
    faqs: [
      { q: "Can storage sheds be built to scale up during peak trading season?", a: "Structures are typically sized for peak seasonal capacity from the start, since expanding mid-season isn't practical — this should be factored into initial planning." },
    ],
  },
  {
    slug: "engineering",
    name: "Engineering & General Fabrication",
    tagline: "Flexible workshop structures for varied fabrication and engineering work.",
    intro:
      "General engineering and fabrication workshops need flexible internal configurations — multiple bays, varied equipment placement and provisions for welding, cutting and assembly work rather than a single uniform production flow.",
    requirements: [
      { title: "Flexible bay configuration", text: "Internal layout planned for multiple work zones rather than one fixed production line." },
      { title: "Provisions for welding & cutting equipment", text: "Ventilation and floor detailing suited to fabrication-shop operations." },
      { title: "Overhead crane options", text: "For handling heavy fabricated components within the workshop." },
      { title: "Durable, low-maintenance flooring", text: "Flooring specified to withstand heavy tool and material traffic." },
    ],
    faqs: [
      { q: "Can a workshop shed be designed with multiple independent bays?", a: "Yes — this is a common configuration for engineering workshops handling varied job types simultaneously." },
    ],
  },
  {
    slug: "packaging",
    name: "Packaging",
    tagline: "Clean, efficient structures for packaging lines and finished-goods storage.",
    intro:
      "Packaging facilities typically combine a processing/packing zone with adjacent finished-goods storage — requiring a structure that balances clean, controlled packing-line conditions with efficient warehouse-style storage in the same building.",
    requirements: [
      { title: "Zoned layout", text: "Structural design accommodating distinct packing-line and storage zones within one facility." },
      { title: "Clean, dust-controlled packing areas", text: "Flooring and finishes suited to hygiene requirements in the packing zone specifically." },
      { title: "Efficient material flow", text: "Layout planned for smooth movement from packing to storage to dispatch." },
    ],
    faqs: [
      { q: "Do packaging facilities need the same hygiene standards as food processing?", a: "It depends on what's being packaged — food-contact packaging often needs similar hygiene considerations, while general goods packaging typically has more flexibility." },
    ],
  },
  {
    slug: "commercial",
    name: "Commercial & Retail",
    tagline: "Large-format commercial structures with architectural flexibility.",
    intro:
      "Commercial and retail structures — showrooms, large-format retail, function halls — use PEB's clear-span capability differently than industrial sheds, prioritizing architectural presentation alongside structural efficiency.",
    requirements: [
      { title: "Architectural finish options", text: "Cladding and facade treatments suited to commercial presentation, beyond standard industrial sheeting." },
      { title: "Clear-span for open floor plans", text: "Column-free interiors for retail floor flexibility or event space use." },
      { title: "Natural light integration", text: "Skylights and glazing options for commercial ambience, not just functional daylighting." },
    ],
    faqs: [
      { q: "Can PEB structures look architecturally finished, not just industrial?", a: "Yes — with appropriate facade cladding, glazing and finishing choices, PEB structures can be designed for commercial presentation rather than a purely industrial appearance." },
    ],
  },
  {
    slug: "cold-storage",
    name: "Cold Storage",
    tagline: "Insulated structures engineered for temperature-controlled storage.",
    intro:
      "Cold storage facilities have structural requirements that differ significantly from standard industrial sheds — vapor barriers, heavy insulation, and structural provisions for refrigeration equipment all need to be designed in from the start, not added afterward.",
    requirements: [
      { title: "PUF panel insulation", text: "Roof and wall insulation specified to the required storage temperature and humidity control needs." },
      { title: "Vapor barrier detailing", text: "Proper vapor barrier design prevents condensation-related structural and product-quality issues." },
      { title: "Refrigeration equipment provisions", text: "Structural accommodation for condenser units, ducting and refrigeration plant placement." },
      { title: "Loading dock thermal breaks", text: "Dock design that minimizes cold-chain breaks during loading and unloading." },
    ],
    faqs: [
      { q: "Can an existing shed be converted to cold storage?", a: "It's possible but often costly, since it typically requires retrofitting insulation and vapor barriers not present in the original design — purpose-built cold storage is usually more cost-effective long-term." },
    ],
  },
  {
    slug: "textiles",
    name: "Textiles",
    tagline: "Wide-span, well-ventilated sheds for spinning, weaving and garmenting.",
    intro:
      "Textile manufacturing — spinning, weaving, garmenting — typically needs wide clear spans for machinery rows and specific ventilation and humidity considerations. Warangal's Kakatiya Textile Park reflects Telangana's established textile manufacturing base.",
    requirements: [
      { title: "Wide clear spans for machinery rows", text: "Column-free layouts to accommodate long rows of spinning or weaving machinery." },
      { title: "Humidity-conscious ventilation", text: "Textile processes are often humidity-sensitive, requiring careful ventilation design." },
      { title: "Dust management provisions", text: "Structural detailing that supports effective dust extraction systems common in textile processing." },
      { title: "Natural lighting for inspection areas", text: "Daylighting design supports quality-inspection zones within the facility." },
    ],
    faqs: [
      { q: "Do textile units typically need special floor specifications?", a: "Generally lighter than heavy-machinery manufacturing, but flooring should still be specified for the vibration and point loads of specific textile machinery in use." },
    ],
  },
  {
    slug: "chemical-process",
    name: "Chemical & Process Industries",
    tagline: "Corrosion-resistant structures built for demanding process environments.",
    intro:
      "Chemical and process-industry facilities operate in environments that can be aggressively corrosive to standard structural steel, requiring specific coating systems and structural detailing — a genuine consideration given the pharma-chemical cluster around Patancheru-Bollaram.",
    requirements: [
      { title: "Enhanced corrosion protection", text: "Higher-grade coating systems specified for chemical exposure environments beyond standard industrial coatings." },
      { title: "Bunding & containment provisions", text: "Structural and flooring design accommodating spill containment requirements common in chemical processing." },
      { title: "Chemical-resistant flooring", text: "Flooring systems selected for resistance to the specific chemicals handled on-site." },
      { title: "Ventilation for fume management", text: "Ventilation design coordinated with process-specific fume extraction requirements." },
    ],
    faqs: [
      { q: "Does chemical exposure significantly change structural cost?", a: "Enhanced coating systems and containment provisions add cost relative to a standard shed, but this investment is essential for structure longevity in a corrosive process environment." },
    ],
  },
];

export const getIndustryBySlug = (slug) => industries.find((i) => i.slug === slug);
